/* Editor di attori e documenti (modale). Applica le modifiche al commit del
   campo (change/blur) per non perdere il focus; add/delete rifanno la lista. */
import type { DiagramDoc, Doc, Participant } from "@fullsteps/shared";
import { el } from "../render/svg.js";
import { modal } from "./modal.js";
import {
  addDoc,
  addParticipant,
  docRefs,
  participantRefs,
  removeDoc,
  removeParticipant,
  slugify,
  updateDoc,
  updateParticipant,
} from "./ops.js";

const ICON_NAMES = ["user", "users", "building-2", "monitor", "hard-hat", "construction"];

export function openEntitiesModal(
  root: HTMLElement,
  doc0: DiagramDoc,
  onChange: (doc: DiagramDoc) => void,
): void {
  const { body } = modal(root, "Attori e documenti");
  let cur = doc0;
  const commit = (d: DiagramDoc): void => {
    cur = d;
    onChange(d);
  };

  const input = (value: string, on: (v: string) => void, ph = ""): HTMLInputElement => {
    const i = el("input", "ed-input sm") as HTMLInputElement;
    i.type = "text";
    i.value = value;
    i.placeholder = ph;
    i.addEventListener("change", () => on(i.value));
    return i;
  };
  const select = (opts: string[], value: string, on: (v: string) => void): HTMLSelectElement => {
    const s = el("select", "ed-input sm") as HTMLSelectElement;
    for (const o of opts) {
      const opt = el("option", undefined, o) as HTMLOptionElement;
      opt.value = o;
      if (o === value) opt.selected = true;
      s.appendChild(opt);
    }
    s.addEventListener("change", () => on(s.value));
    return s;
  };
  const color = (value: string, on: (v: string) => void): HTMLInputElement => {
    const i = el("input", "ed-color") as HTMLInputElement;
    i.type = "color";
    i.value = value;
    i.addEventListener("change", () => on(i.value));
    return i;
  };
  const delBtn = (fn: () => void): HTMLButtonElement => {
    const b = el("button", "ed-iconbtn", "🗑") as HTMLButtonElement;
    b.type = "button";
    b.title = "Elimina";
    b.addEventListener("click", fn);
    return b;
  };

  const render = (): void => {
    body.textContent = "";
    body.appendChild(el("h3", "ed-h3", "Attori"));
    for (const p of cur.participants) body.appendChild(partRow(p));
    const addP = el("button", "btn sm", "＋ Aggiungi attore") as HTMLButtonElement;
    addP.type = "button";
    addP.addEventListener("click", () => {
      const ids = new Set(cur.participants.map((x) => x.id));
      const id = slugify("attore", ids);
      commit(
        addParticipant(cur, {
          id, name: "Nuovo attore", kind: "participant", icon: "user",
          colors: { dark: "#8a9099", light: "#5b6169" },
        }),
      );
      render();
    });
    body.appendChild(addP);

    body.appendChild(el("h3", "ed-h3", "Documenti"));
    for (const d of cur.docs) body.appendChild(docRow(d));
    const addD = el("button", "btn sm", "＋ Aggiungi documento") as HTMLButtonElement;
    addD.type = "button";
    addD.addEventListener("click", () => {
      const ids = new Set(cur.docs.map((x) => x.id));
      const id = slugify("doc", ids);
      commit(addDoc(cur, { id, short: "Nuovo", label: "Nuovo documento" }));
      render();
    });
    body.appendChild(addD);
  };

  const partRow = (p: Participant): HTMLElement => {
    const row = el("div", "ent-row");
    row.appendChild(el("code", "ent-id", p.id));
    row.appendChild(input(p.name, (v) => commit(updateParticipant(cur, p.id, { name: v })), "nome"));
    row.appendChild(select(["participant", "actor"], p.kind, (v) => commit(updateParticipant(cur, p.id, { kind: v as Participant["kind"] }))));
    row.appendChild(select(ICON_NAMES, p.icon, (v) => commit(updateParticipant(cur, p.id, { icon: v }))));
    row.appendChild(color(p.colors.dark, (v) => {
      const cp = cur.participants.find((x) => x.id === p.id)!;
      commit(updateParticipant(cur, p.id, { colors: { ...cp.colors, dark: v } }));
    }));
    row.appendChild(color(p.colors.light, (v) => {
      const cp = cur.participants.find((x) => x.id === p.id)!;
      commit(updateParticipant(cur, p.id, { colors: { ...cp.colors, light: v } }));
    }));
    const tempL = el("label", "ed-check");
    const temp = el("input") as HTMLInputElement;
    temp.type = "checkbox";
    temp.checked = !!p.temp;
    temp.addEventListener("change", () => commit(updateParticipant(cur, p.id, { temp: temp.checked || undefined })));
    tempL.append(temp, document.createTextNode(" temp"));
    row.appendChild(tempL);
    row.appendChild(delBtn(() => {
      const refs = participantRefs(cur, p.id);
      if (refs) {
        row.appendChild(el("span", "login-err", `usato in ${refs} passi`));
        return;
      }
      commit(removeParticipant(cur, p.id));
      render();
    }));
    return row;
  };

  const docRow = (d: Doc): HTMLElement => {
    const row = el("div", "ent-row");
    row.appendChild(el("code", "ent-id", d.id));
    row.appendChild(input(d.short, (v) => commit(updateDoc(cur, d.id, { short: v })), "sigla"));
    row.appendChild(input(d.label, (v) => commit(updateDoc(cur, d.id, { label: v })), "etichetta estesa"));
    row.appendChild(input(d.group ?? "", (v) => commit(updateDoc(cur, d.id, { group: v || undefined })), "gruppo"));
    row.appendChild(delBtn(() => {
      const refs = docRefs(cur, d.id);
      if (refs) {
        row.appendChild(el("span", "login-err", `usato in ${refs} passi`));
        return;
      }
      commit(removeDoc(cur, d.id));
      render();
    }));
    return row;
  };

  render();
}
